import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    FormGroup,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import { ViewMode } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import fastDeepEqual from 'fast-deep-equal';
import { distinctUntilChanged } from 'rxjs';
import { fliFxStampKeyOf } from '../../interfaces/IFxNotesContext.interface';
import { ITableWithForm } from '../tableWithForm/ITableWithForm.interface';

@UntilDestroy()
@Component({
    selector: 'fli-component-table',
    templateUrl: 'fli-component-table.component.html',
    styleUrls: ['fli-component-table.component.scss'],
})
export class FliComponentTableComponent {
    public form: UntypedFormGroup;
    public localizedTotalText: string;

    DATA?: any[];
    @Input() public set data(data: any[]) {
        if (data) {
            this.DATA = data;
            this.updateForm();
        }
    }

    totalRowTextCell: string;
    @Input() set totalRowText(value: string) {
        this.totalRowTextCell = value;
        this.updateForm();
    }

    @Input() viewMode = ViewMode.PREVIEW;
    @Input() isNumbered = false;
    @Input() isReferenced = false;
    @Input() tableNumber: string | undefined;
    @Input() textTitle: string | undefined;
    @Input() language: string;
    @Input() selectedFliName: string | undefined;
    @Input() todoHighlight = true;
    @Input() editable = true;
    @Input() previousDate: string | undefined;
    @Input() currentDate: string | undefined;

    /**
     * FX (REV-21512): which currency columns this table shows — driven by the 3-state chip.
     * `functional` renders exactly the legacy table (G0). Display cells are computed from the
     * per-account stamps and are read-only — never form-controlled, never persisted (E2).
     */
    @Input() fxState: 'functional' | 'both' | 'display' = 'functional';
    /**
     * True on FX reports only. Drives the currency line under each date header — an FX report
     * labels its columns in every state, a normal report never does (no regression).
     */
    @Input() fxEnabled = false;
    /** Per-account display values, keyed `id|accountName` (built by the app's FlisMapper walk). */
    @Input() set fliFxStamps(value: Map<string, { displayCY: number | null; displayPY: number | null }> | null) {
        this._fliFxStamps = value;
        this.recomputeFxTotals();
    }
    get fliFxStamps(): Map<string, { displayCY: number | null; displayPY: number | null }> | null {
        return this._fliFxStamps;
    }
    private _fliFxStamps: Map<string, { displayCY: number | null; displayPY: number | null }> | null = null;
    /** Memoized column totals — recomputed on data/stamps changes, not per change-detection tick. */
    fxTotalCY: number | null = null;
    fxTotalPY: number | null = null;
    @Input() functionalCurrency = '';
    @Input() displayCurrency = '';

    fxDisplayOf(row: { value?: { id?: unknown; accountName?: string } }, year: 'cy' | 'py'): number | null {
        if (this.fliFxStamps == null || row?.value == null) {
            return null;
        }
        const stamp = this.fliFxStamps.get(fliFxStampKeyOf(row.value));
        if (stamp == null) {
            return null;
        }

        return year === 'cy' ? stamp.displayCY : stamp.displayPY;
    }

    /**
     * Disabled controls for the display cells so they render through the SAME hamInput fields as
     * the functional cells — identical look. Deliberately NOT part of `this.form`: nothing here
     * may ever travel into the saved blob (E2), the values are computed.
     */
    private readonly fxCellControls = new Map<string, UntypedFormControl>();

    fxCellControl(row: { value?: { id?: unknown; accountName?: string } }, year: 'cy' | 'py'): UntypedFormControl {
        const key = `${fliFxStampKeyOf(row?.value ?? {})}|${year}`;
        const value = this.fxDisplayOf(row, year);
        let control = this.fxCellControls.get(key);
        if (control == null) {
            control = new UntypedFormControl({ value, disabled: true });
            this.fxCellControls.set(key, control);
        } else if (control.value !== value) {
            control.setValue(value, { emitEvent: false });
        }

        return control;
    }

    fxTotalControl(year: 'cy' | 'py'): UntypedFormControl {
        const key = `__total__|${year}`;
        const value = year === 'cy' ? this.fxTotalCY : this.fxTotalPY;
        let control = this.fxCellControls.get(key);
        if (control == null) {
            // Disabled like the functional total controls (createTotalRowGroup) so both render identically.
            control = new UntypedFormControl({ value, disabled: true });
            this.fxCellControls.set(key, control);
        } else if (control.value !== value) {
            control.setValue(value, { emitEvent: false });
        }

        return control;
    }

    /**
     * Sum of the rows' display values (each already rounded per account — G.1). All-or-nothing
     * like every other FX total (super-table editor/print, FLI print): ANY missing value nulls
     * the total — a partial sum would look settled while excluding rows.
     */
    private fxTotalOf(year: 'cy' | 'py'): number | null {
        const rows = (this.form?.get('dataSource') as UntypedFormArray)?.controls ?? [];
        if (rows.length === 0) {
            return null;
        }
        const values = rows.map((row) =>
            this.fxDisplayOf(row as { value?: { id?: unknown; accountName?: string } }, year),
        );
        if (values.some((value) => value == null)) {
            return null;
        }

        return NumberUtils.sumIgnoringInfiniteNumbers(values as number[], 0);
    }

    private recomputeFxTotals(): void {
        this.fxTotalCY = this.fxTotalOf('cy');
        this.fxTotalPY = this.fxTotalOf('py');
    }

    @Input() hasFootnotes: boolean = false;
    @Input() set savedFootnote(value: string | undefined) {
        if (value !== undefined) {
            this.currentFootnoteValue = value || '';
            this.footnotes = value ? [value] : [];

            // Update form control if it exists
            if (this.form?.get('footnote')) {
                this.form.get('footnote').setValue(this.currentFootnoteValue, { emitEvent: false });
            }
        }
    }

    footnotes: string[] = [];
    currentFootnoteValue: string = '';

    @Output() formChange: EventEmitter<ITableWithForm> = new EventEmitter();
    @Output() onAddNumberingClicked: EventEmitter<void> = new EventEmitter();
    @Output() onAddReferencingClicked: EventEmitter<void> = new EventEmitter();

    constructor(private formBuilder: UntypedFormBuilder, private i18nextService: I18NextService) {
        this.applyLocalizations();
    }

    private handleFootnoteChanges(): void {
        const footnoteValue = this.form.value.footnote;
        if (footnoteValue != null && footnoteValue !== '') {
            this.footnotes = [footnoteValue];
            this.currentFootnoteValue = footnoteValue;
        } else {
            this.footnotes = [];
            this.currentFootnoteValue = '';
        }
    }

    private applyLocalizations(): void {
        this.localizedTotalText = this.i18nextService.t('aumico_notes_table_with_form_total') as string;
    }

    public updateForm(): void {
        this.form = this.formBuilder.group({
            dataSource: this.formBuilder.array([]),
            totalRow: this.createTotalRowGroup(),
            footnote: new UntypedFormControl(this.currentFootnoteValue || ''),
        });

        const previousEntriesValue = this.DATA.map<number | null>((data) => data.previousValue);
        const currentEntriesValue = this.DATA.map<number | null>((data) => data.currentValue);
        const previousSum = NumberUtils.sumIgnoringInfiniteNumbers(previousEntriesValue, 0);
        const currentSum = NumberUtils.sumIgnoringInfiniteNumbers(currentEntriesValue, 0);

        const dataSource = this.getAllAsFormArray();
        this.form.setControl('dataSource', dataSource);
        this.recomputeFxTotals();

        this.form.get('totalRow').get('accountName').setValue(this.totalRowTextCell, { emitEvent: false });
        this.form.get('totalRow').get('currentValue').setValue(currentSum, { emitEvent: false });
        this.form.get('totalRow').get('previousValue').setValue(previousSum, { emitEvent: false });

        this.form.valueChanges
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.DATA = this.form?.getRawValue().dataSource;
                // accountName is half the stamp key — a rename must drop the row from the total.
                this.recomputeFxTotals();

                this.handleFootnoteChanges();

                const totalRow = this.form?.getRawValue().totalRow;

                this.formChange.emit({
                    formValue: this.form?.getRawValue().dataSource,
                    totalRowValue: totalRow,
                    footnote: this.currentFootnoteValue,
                });
            });
    }

    createTotalRowGroup(): FormGroup {
        this.totalRowTextCell = this.totalRowTextCell ?? this.localizedTotalText;

        return this.formBuilder.group({
            accountName: new UntypedFormControl(this.totalRowTextCell, Validators.required),
            currentValue: new UntypedFormControl({ value: 0, disabled: true }, Validators.required),
            previousValue: new UntypedFormControl({ value: 0, disabled: true }, Validators.required),
        });
    }

    private getAllAsFormArray(): UntypedFormArray {
        const formGroups = this.DATA.map((data) => this.addFormGroup(data));
        return new UntypedFormArray(formGroups);
    }

    private addFormGroup(dataSource: any): UntypedFormGroup {
        const dataSourceFormGroup = {};
        Object.keys(dataSource).forEach((key) => {
            if (key !== 'remove') {
                dataSourceFormGroup[key] = new UntypedFormControl(dataSource[key]);
            }
        });
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    onAddReferencingClick(): void {
        this.onAddReferencingClicked.emit();
    }

    onAddNumberingClick(): void {
        this.onAddNumberingClicked.emit();
    }
}
